import * as React from "react"
import { Col, Container, Row, Visible } from "react-grid-system"
import { useTranslation } from "react-i18next"
import Subtitle from "../../atoms/typography/Subtitle"
import IconButton from "../../atoms/buttons/IconButton"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import { SettingsIcon } from "../../atoms/icons/SettingsIcon"
import Dropdown, { optionsMap } from "../../atoms/inputs/Dropdown"
import SearchField from "../../atoms/inputs/SearchField"
import CreateSuborganizationOrganism from "../../organisms/CreateSuborganizationOrganism"
import SuborganizationOverview from "../../organisms/SuborganizationsOverview"
import { useMyOrgSuborgs } from "../../../hooks/useMyOrgSuborgs"
import { getFilteredSuborganizations } from "../../../utils/formatters"
import { OrganizationType } from "../../../utils/models"
import DetailsIcon from "../../atoms/icons/DetailsIcon"
import ListIcon from "../../atoms/icons/ListIcon"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import { theme } from "../../theme"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import { usePagination } from "../../../hooks/usePagination"
import PaginationComponent from "../../molecules/PaginationComponent"
import H4 from "../../atoms/typography/H4"
import Body from "../../atoms/typography/Body"
import { useNavigate, useParams } from "react-router"
import { Key } from "react-aria-components"
import Button from "../../atoms/buttons/Button"

const DROPDOWN_OPTIONS = [
  { id: "name", value: "name", name: "Name A-Z" },
  { id: "-name", value: "-name", name: "Name Z-A" },
  { id: "-members", value: "-members", name: "Members high - low" },
  { id: "members", value: "members", name: "Members low - high" },
  { id: "-terminals", value: "-terminals", name: "Terminals high - low" },
  { id: "terminals", value: "terminals", name: "Terminals low - high" },
  { id: "-groups", value: "-groups", name: "Groups high - low" },
  { id: "groups", value: "groups", name: "Groups low - high" },
]
const SuborganizationsPage: React.FunctionComponent = () => {
  const { t } = useTranslation()

  const [searchValue, setSearchValue] = React.useState<undefined | string>(undefined)
  const [sortingId, setSortingId] = React.useState<Key>("name")
  const [cardsOpen, setCardsOpen] = React.useState<Array<string>>([])
  const [isEditing, setIsEditing] = React.useState(false)

  const sorting = optionsMap(DROPDOWN_OPTIONS)[sortingId].value!

  // used insteand of defualt because the query cost for sub orgs is very high
  const SUB_ORGS_ITEMS_PER_PAGE_VALUES = [10, 15, 20, 25]
  const navigate = useNavigate()
  const { page } = useParams()
  const pageIndex = parseInt(page ?? "1")
  const { first, skip, getPaginationProps } = usePagination(
    SUB_ORGS_ITEMS_PER_PAGE_VALUES,
    pageIndex,
    (index) => "/organization/suborganizations/" + index,
  )
  const { myOrgSuborgs, orgLoading } = useMyOrgSuborgs(first, skip, sorting, searchValue)

  const onSearch = (value: string) => {
    setSearchValue(value)
  }

  const suborgList = getFilteredSuborganizations(myOrgSuborgs?.edges ?? [])

  //Handle open sections
  const checkCardIsOpen = (id: string) => {
    return cardsOpen.includes(id)
  }
  const onClickCloseCard = (id: string) => {
    if (checkCardIsOpen(id)) {
      const arr = cardsOpen.filter((x) => x !== id)
      setCardsOpen(arr)
    } else {
      setCardsOpen([...cardsOpen, id])
    }
  }
  const onClickListView = () => {
    setCardsOpen([])
  }
  const allGroupsIds: Array<string> = []
  suborgList.map((g: any) => allGroupsIds.push(g.id))
  const onClickDetailsView = () => {
    setCardsOpen(allGroupsIds)
  }

  return (
    <div>
      <Row align="end">
        <Col lg={2} md={4} xs={12}>
          <SearchField onChange={(e) => onSearch(e)} />
          <Visible xs>
            <HorizontalSpacer spacing={2} />
          </Visible>
        </Col>
        <Col lg={2} md={4} xs={7}>
          <Dropdown
            options={DROPDOWN_OPTIONS}
            label={t("sort_by")}
            onSelectionChange={(id) => setSortingId(id)}
            selectedKey={sortingId}
          />
        </Col>
        <Col lg={2} md={3} xs={5}>
          <div style={{ display: "flex" }}>
            <Button
              iconLeft={<ListIcon small color={cardsOpen.length < 1 ? theme.colors.white : theme.colors.greyDark} />}
              onPress={onClickListView}
              variant={cardsOpen.length < 1 ? "dark" : "light"}
            />
            <Spacer spacing={1} />
            <Button
              onPress={onClickDetailsView}
              variant={cardsOpen.length === allGroupsIds.length ? "dark" : "light"}
              iconLeft={
                <DetailsIcon
                  small
                  color={cardsOpen.length === allGroupsIds.length ? theme.colors.white : theme.colors.greyDark}
                />
              }
            />
          </div>
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />
      <Row justify="between" style={{ padding: "0 15px" }}>
        <Subtitle>{t("suborganizations")}</Subtitle>
        {!isEditing && (
          <PrimaryButton title="Manage suborganizations" icon={<SettingsIcon />} onClick={() => setIsEditing(true)} />
        )}
        {isEditing && <SecondaryButton title="Done" onClick={() => setIsEditing(false)} />}
      </Row>
      <HorizontalSpacer spacing={2} />
      {isEditing && (
        <div>
          <CreateSuborganizationOrganism />
          <HorizontalSpacer spacing={2} />
        </div>
      )}
      {orgLoading ? (
        <LoadingSpinner />
      ) : (
        suborgList.map((suborg: OrganizationType) => (
          <div key={suborg.id}>
            <SuborganizationOverview
              pendingInvites={suborg.pendingInvitations ?? []}
              organization={suborg}
              isEditing={isEditing}
              open={checkCardIsOpen(suborg.id)}
              onClickClose={() => onClickCloseCard(suborg.id)}
            />
            <HorizontalSpacer spacing={2} />
          </div>
        ))
      )}
      <Row justify="center">
        <PaginationComponent {...getPaginationProps(myOrgSuborgs?.totalCount ?? 0)} />
      </Row>
      <Container>
        <HorizontalSpacer spacing={3} />
        <H4>What are sub-organizations? </H4>
        <HorizontalSpacer spacing={1} />
        <Body>
          Sub-organizations are autonomous organizations which can be viewed and administered from the main
          organization. Sub-organizations have independent billing information and conduct purchases separately from the
          main organization. Terminals cannot be shared between sub-organizations and not between sub-organizations and
          the main organization.
          <HorizontalSpacer spacing={1} />
          Creating a sub-organization is suitable for subsidiaries and other independently operating parts of your
          organization which need to conduct purchases and use terminals separately from the main organization. Members
          of sub-organizations cannot view the main organization, while authorized members of the main organization can
          view and administrate the sub-organizations.
        </Body>
        <HorizontalSpacer spacing={1} />
        <H4>Should I create a Group or a Sub-organization?</H4>
        <HorizontalSpacer spacing={1} />
        <Body>
          Groups are best used within your organization to keep track of who has access to what, and which terminals are
          being used for which projects. Groups are also useful for shorter projects and enable a clear oversight within
          your organization.
          <HorizontalSpacer spacing={1} />
          Sub-organizations are separate organizations which can be viewed and accessed by authorized personnel from the
          main organization. Sub-organizations can make purchases and have groups of their own and are useful for
          subsidiaries and independently operating parts of your organization.
          <HorizontalSpacer spacing={1} />
          Terminals can be shared between groups, but not between organizations and sub-organizations.
        </Body>
        <HorizontalSpacer spacing={3} />
      </Container>
    </div>
  )
}

export default SuborganizationsPage
